import { useTopLevelAssemblies } from '@/resources/assembly/assemblyHandler';
import { useSites } from '@/resources/organizationSettings/sitesHandler';
import { useDefaultSourcingTemplate } from '@/resources/sourcingTemplate/sourcingTemplateHandler';
import { useSupplierAndStockLocations } from '@/resources/supplierAndStockLocation/supplierAndStockLocationHandler';
import { isPresent } from '@luminovo/commons';
import {
    AssemblyResponseDTO,
    InventoryPreferenceRuntype,
    LimitType,
    PackagingRuntype,
    PriceType,
    SiteDTO,
    SiteRuntype,
    SolutionComparisonCriteria,
    SourcingPreferenceOptions,
    SourcingScenarioDTO,
    SourcingScenarioPatchDTO,
    SourcingScenarioPostDTO,
    SourcingTemplateDTO,
    SourcingTemplateDTORuntype,
    SourcingTemplateLimit,
    SourcingTemplateTags,
    SupplierAndStockLocationDTO,
    SupplierAndStockLocationDTORuntype,
    SupplierPreference,
    SupplierPreferenceRuntype,
    TotalCostOfOwnershipRuleDTORuntype,
} from '@luminovo/http-client';
import { z } from 'zod';
import {
    convertDemandFormStateToRequestBody,
    createDemandFormStateDefaultValues,
    DemandFormStateSchema,
} from './DemandDialog/DemandFormItems';

export type SourcingScenarioTab =
    | 'general'
    | 'solution-preference'
    | 'solution-tags-preferences'
    | 'supplier-preferences'
    | 'inventory-preferences'
    | 'total-cost-of-ownership-rules';

export type SourcingScenarioFormValues = z.infer<typeof sourcingScenarioFormValueSchema>;
export const sourcingScenarioFormValueSchema = z.object({
    general: z.object({
        rfq: z.string(),
        name: z.string().min(1, `Name is required`),
        sourcingTemplate: SourcingTemplateDTORuntype.nullable(),
    }),
    assemblyDemands: z
        .array(
            z.discriminatedUnion('kind', [
                z.object({ kind: z.literal('existing'), data: z.object({ id: z.string() }) }),
                z.object({
                    kind: z.literal('new'),
                    data: DemandFormStateSchema,
                }),
            ]),
        )
        .min(1, 'At least one demand must be selected'),
    solutionPreferences: z.object({
        leadTimePreference: z.object({
            type: z.nativeEnum(SourcingPreferenceOptions),
            target: z.string().optional(),
        }),
        solutionComparisonCriteria: z.nativeEnum(SolutionComparisonCriteria),
        excludedPriceTypes: z.object({
            listPrice: z.boolean(),
            contractPrice: z.boolean(),
            quotePrice: z.boolean(),
            customerNegotiatedPrice: z.boolean(),
            purchasePrice: z.boolean(),
            standardPrice: z.boolean(),
        }),
        inventoryPreference: InventoryPreferenceRuntype,
        packagingPreference: z.array(PackagingRuntype),
        preferDirectApiIntegrations: z.boolean(),
        excludeMismatchedPricePoints: z.boolean(),
        prioritizePreferredManufacturers: z.boolean(),
        preferCustomerNegotiatedPrices: z.boolean(),
        isTcoEnabled: z.boolean(),
    }),
    solutionTagsPreferences: z.object({
        offerAgeLimit: z.object({
            oldAfterUpdateDays: z.number().nullable(),
            outdatedAfterUpdateDays: z.number().nullable(),
            expiringBeforeValidityDate: z.number().nullable(),
            expiredAfterValidityDate: z.number().nullable(),
        }),
    }),
    supplierPreferences: z.array(
        SupplierAndStockLocationDTORuntype.extend({
            preference: SupplierPreferenceRuntype,
        }),
    ),
    inventoryPreferences: z.array(
        SiteRuntype.extend({
            preference: SupplierPreferenceRuntype,
        }),
    ),
    totalCostOfOwnershipRules: z.array(TotalCostOfOwnershipRuleDTORuntype),
});

function convertSolutionPreferences(
    formValues: SourcingScenarioFormValues,
): SourcingScenarioPostDTO['solution_preference'] {
    const getExcludedPriceTypes = () =>
        [
            formValues.solutionPreferences.excludedPriceTypes.listPrice ? PriceType.ListPrice : undefined,
            formValues.solutionPreferences.excludedPriceTypes.contractPrice ? PriceType.ContractPrice : undefined,
            formValues.solutionPreferences.excludedPriceTypes.quotePrice ? PriceType.QuotePrice : undefined,
            formValues.solutionPreferences.excludedPriceTypes.customerNegotiatedPrice
                ? PriceType.CustomerNegotiatedPrice
                : undefined,
            formValues.solutionPreferences.excludedPriceTypes.purchasePrice ? PriceType.PurchasePrice : undefined,
            formValues.solutionPreferences.excludedPriceTypes.standardPrice ? PriceType.StandardPrice : undefined,
        ].filter(isPresent);

    const getOfferAgeLimit = (): SourcingScenarioPostDTO['solution_preference']['offer_age_limit'] => {
        const convertToLimitType = (days: number | null) =>
            isPresent(days) ? { type: LimitType.Days, number: days } : { type: LimitType.Never, number: undefined };

        return {
            old_after_update: convertToLimitType(formValues.solutionTagsPreferences.offerAgeLimit.oldAfterUpdateDays),
            outdated_after_update: convertToLimitType(
                formValues.solutionTagsPreferences.offerAgeLimit.outdatedAfterUpdateDays,
            ),
            expiring_before_validity_date: convertToLimitType(
                formValues.solutionTagsPreferences.offerAgeLimit.expiringBeforeValidityDate,
            ),
            expired_after_validity_date: convertToLimitType(
                formValues.solutionTagsPreferences.offerAgeLimit.expiredAfterValidityDate,
            ),
        };
    };

    const getLeadTimePreference = (): SourcingScenarioPostDTO['solution_preference']['lead_time_preference'] => {
        if (formValues.solutionPreferences.leadTimePreference.type === SourcingPreferenceOptions.BestPriceBy) {
            return {
                type: formValues.solutionPreferences.leadTimePreference.type,
                target: formValues.solutionPreferences.leadTimePreference.target,
            };
        }
        return { type: formValues.solutionPreferences.leadTimePreference.type, target: undefined };
    };

    const getTotalCostOfOwnershipRules =
        (): SourcingScenarioPostDTO['solution_preference']['total_cost_of_ownership_rules'] => {
            return formValues.totalCostOfOwnershipRules.map((tcoRule) => ({
                name: tcoRule.name,
                description: tcoRule.description,
                category: tcoRule.category,
                action: tcoRule.action,
                conditions: tcoRule.conditions,
            }));
        };

    return {
        lead_time_preference: getLeadTimePreference(),
        use_unit_price:
            formValues.solutionPreferences.solutionComparisonCriteria === SolutionComparisonCriteria.UnitPrice,
        excluded_price_types: getExcludedPriceTypes(),
        offer_age_limit: getOfferAgeLimit(),
        inventory_preference: formValues.solutionPreferences.inventoryPreference,
        packaging_preference: formValues.solutionPreferences.packagingPreference,
        prefer_direct_api_integrations: formValues.solutionPreferences.preferDirectApiIntegrations,
        exclude_mismatched_price_points: formValues.solutionPreferences.excludeMismatchedPricePoints,
        prioritize_preferred_manufacturers: formValues.solutionPreferences.prioritizePreferredManufacturers,
        prefer_customer_negotiated_prices: formValues.solutionPreferences.preferCustomerNegotiatedPrices,
        is_tco_enabled: formValues.solutionPreferences.isTcoEnabled,
        preferred_suppliers_and_stock_locations: convertSupplierPreferences(formValues, SupplierPreference.Preferred),
        approved_suppliers_and_stock_locations: convertSupplierPreferences(formValues, SupplierPreference.Approved),
        excluded_suppliers_and_stock_locations: convertSupplierPreferences(formValues, SupplierPreference.Excluded),
        preferred_inventory_sites: convertInventoryPreferences(formValues, SupplierPreference.Preferred),
        approved_inventory_sites: convertInventoryPreferences(formValues, SupplierPreference.Approved),
        excluded_inventory_sites: convertInventoryPreferences(formValues, SupplierPreference.Excluded),
        total_cost_of_ownership_rules: getTotalCostOfOwnershipRules(),
    };
}

function convertAssemblyDemands(formValues: SourcingScenarioFormValues): SourcingScenarioPostDTO['assembly_demands'] {
    return formValues.assemblyDemands
        .map((ad) => {
            if (ad.kind === 'existing') {
                return { kind: 'existing' as const, data: { id: ad.data.id } };
            }
            if (ad.kind === 'new') {
                return { kind: 'new' as const, data: convertDemandFormStateToRequestBody(ad.data) };
            }
            return null;
        })
        .filter(isPresent);
}

function convertSupplierPreferences(
    formValues: SourcingScenarioFormValues,
    supplierPreferenceType: SupplierPreference,
): SourcingScenarioPostDTO['solution_preference']['preferred_suppliers_and_stock_locations'] {
    return formValues.supplierPreferences.filter((sp) => sp.preference === supplierPreferenceType).map((sp) => sp.id);
}

function convertInventoryPreferences(
    formValues: SourcingScenarioFormValues,
    supplierPreferenceType: SupplierPreference,
): SourcingScenarioPostDTO['solution_preference']['preferred_inventory_sites'] {
    return formValues.inventoryPreferences.filter((ip) => ip.preference === supplierPreferenceType).map((ip) => ip.id);
}

export function convertSourcingScenarioFormValuesToPatchDTO(
    formValues: SourcingScenarioFormValues,
): SourcingScenarioPatchDTO {
    return {
        name: formValues.general.name,
        assembly_demands: convertAssemblyDemands(formValues),
        solution_preference: convertSolutionPreferences(formValues),
        origin_sourcing_template_id: formValues.general.sourcingTemplate?.id ?? null,
    };
}

export function convertSourcingScenarioFormValuesToPostDTO(
    formValues: SourcingScenarioFormValues,
): SourcingScenarioPostDTO {
    return {
        rfq: formValues.general.rfq,
        name: formValues.general.name,
        assembly_demands: convertAssemblyDemands(formValues),
        solution_preference: convertSolutionPreferences(formValues),
        origin_sourcing_template_id: formValues.general.sourcingTemplate?.id ?? null,
    };
}

export function createDefaultSourcingScenarioFormValues({
    rfqId,
    sourcingTemplate,
    assemblies,
    supplierAndStockLocations,
    inventorySites,
}: {
    rfqId: string;
    sourcingTemplate: SourcingTemplateDTO;
    assemblies: AssemblyResponseDTO[];
    supplierAndStockLocations: SupplierAndStockLocationDTO[];
    inventorySites: SiteDTO[];
}): SourcingScenarioFormValues {
    const getOfferAgeLimit = (): SourcingScenarioFormValues['solutionTagsPreferences']['offerAgeLimit'] => {
        const convertToLimitType = (value: SourcingTemplateLimit): number | null =>
            value.type === 'Days' && isPresent(value.number) ? value.number : null;

        return {
            oldAfterUpdateDays: convertToLimitType(
                sourcingTemplate.solution_preference.offer_age_limit.old_after_update,
            ),
            outdatedAfterUpdateDays: convertToLimitType(
                sourcingTemplate.solution_preference.offer_age_limit.outdated_after_update,
            ),
            expiringBeforeValidityDate: convertToLimitType(
                sourcingTemplate.solution_preference.offer_age_limit.expiring_before_validity_date,
            ),
            expiredAfterValidityDate: convertToLimitType(
                sourcingTemplate.solution_preference.offer_age_limit.expired_after_validity_date,
            ),
        };
    };

    const getSupplierPreferences = (): SourcingScenarioFormValues['supplierPreferences'] => {
        return supplierAndStockLocations
            .map((sp) => ({
                ...sp,
                preference:
                    sourcingTemplate.supplier_and_stock_location_preferences.find(
                        (spp) => spp.supplier_and_stock_location_id === sp.id,
                    )?.kind ?? SupplierPreference.NotApproved,
            }))
            .sort((a, b) => {
                const PREFERENCE_SORT_ORDER: Record<SupplierPreference, number> = {
                    [SupplierPreference.Preferred]: 0,
                    [SupplierPreference.Approved]: 1,
                    [SupplierPreference.NotApproved]: 2,
                    [SupplierPreference.Excluded]: 3,
                };
                return PREFERENCE_SORT_ORDER[a.preference] - PREFERENCE_SORT_ORDER[b.preference];
            });
    };

    const getInventoryPreferences = (): SourcingScenarioFormValues['inventoryPreferences'] => {
        return inventorySites
            .map((ip) => ({
                ...ip,
                preference:
                    sourcingTemplate.inventory_site_preferences.find((ipp) => ipp.inventory_site_id === ip.id)?.kind ??
                    SupplierPreference.NotApproved,
            }))
            .sort((a, b) => {
                const PREFERENCE_SORT_ORDER: Record<SupplierPreference, number> = {
                    [SupplierPreference.Preferred]: 0,
                    [SupplierPreference.Approved]: 1,
                    [SupplierPreference.NotApproved]: 2,
                    [SupplierPreference.Excluded]: 3,
                };
                return PREFERENCE_SORT_ORDER[a.preference] - PREFERENCE_SORT_ORDER[b.preference];
            });
    };

    return {
        general: {
            rfq: rfqId,
            name: '',
            sourcingTemplate,
        },
        assemblyDemands: assemblies.map((assembly) => ({
            kind: 'new',
            data: createDemandFormStateDefaultValues(rfqId, { assembly }),
        })),
        solutionPreferences: {
            leadTimePreference: {
                // TODO: FIX THIS
                type: sourcingTemplate.solution_preference.lead_time_preferences
                    .type as unknown as SourcingPreferenceOptions,
                target: sourcingTemplate.solution_preference.lead_time_preferences.target,
            },
            solutionComparisonCriteria: sourcingTemplate.solution_preference.solution_comparison_criteria,
            excludedPriceTypes: {
                listPrice: sourcingTemplate.solution_preference.excluded_price_types.includes(PriceType.ListPrice),
                contractPrice: sourcingTemplate.solution_preference.excluded_price_types.includes(
                    PriceType.ContractPrice,
                ),
                quotePrice: sourcingTemplate.solution_preference.excluded_price_types.includes(PriceType.QuotePrice),
                customerNegotiatedPrice: sourcingTemplate.solution_preference.excluded_price_types.includes(
                    PriceType.CustomerNegotiatedPrice,
                ),
                purchasePrice: sourcingTemplate.solution_preference.excluded_price_types.includes(
                    PriceType.PurchasePrice,
                ),
                standardPrice: sourcingTemplate.solution_preference.excluded_price_types.includes(
                    PriceType.StandardPrice,
                ),
            },
            inventoryPreference: sourcingTemplate.solution_preference.inventory_preference,
            packagingPreference: sourcingTemplate.solution_preference.packaging_preferences,
            preferDirectApiIntegrations: sourcingTemplate.solution_preference.prefer_direct_api_integrations,
            excludeMismatchedPricePoints: sourcingTemplate.solution_preference.exclude_mismatched_price_points,
            prioritizePreferredManufacturers: sourcingTemplate.solution_preference.prioritize_preferred_manufacturers,
            preferCustomerNegotiatedPrices: sourcingTemplate.solution_preference.prefer_customer_negotiated_prices,
            isTcoEnabled: sourcingTemplate.solution_preference.is_tco_enabled,
        },
        solutionTagsPreferences: {
            offerAgeLimit: getOfferAgeLimit(),
        },
        supplierPreferences: getSupplierPreferences(),
        inventoryPreferences: getInventoryPreferences(),
        totalCostOfOwnershipRules: sourcingTemplate.total_cost_of_ownership_rules,
    };
}

export function convertSourcingScenarioDTOToFormValues({
    sourcingScenario,
    supplierAndStockLocations,
    inventorySites,
    sourcingTemplates,
}: {
    sourcingScenario: SourcingScenarioDTO;
    supplierAndStockLocations: SupplierAndStockLocationDTO[];
    inventorySites: SiteDTO[];
    sourcingTemplates: SourcingTemplateDTO[];
}): SourcingScenarioFormValues {
    const getOfferAgeLimit = (): SourcingScenarioFormValues['solutionTagsPreferences']['offerAgeLimit'] => {
        const convertToLimitType = (value: SourcingTemplateLimit): number | null =>
            value.type === 'Days' && isPresent(value.number) ? value.number : null;

        return {
            oldAfterUpdateDays: convertToLimitType(
                sourcingScenario.solution_preference.offer_age_limit.old_after_update,
            ),
            outdatedAfterUpdateDays: convertToLimitType(
                sourcingScenario.solution_preference.offer_age_limit.outdated_after_update,
            ),
            expiringBeforeValidityDate: convertToLimitType(
                sourcingScenario.solution_preference.offer_age_limit.expiring_before_validity_date,
            ),
            expiredAfterValidityDate: convertToLimitType(
                sourcingScenario.solution_preference.offer_age_limit.expired_after_validity_date,
            ),
        };
    };

    const getSupplierPreferences = (): SourcingScenarioFormValues['supplierPreferences'] => {
        return supplierAndStockLocations
            .map((sp) => ({
                ...sp,
                preference: sourcingScenario.solution_preference.preferred_suppliers_and_stock_locations?.includes(
                    sp.id,
                )
                    ? SupplierPreference.Preferred
                    : sourcingScenario.solution_preference.approved_suppliers_and_stock_locations?.includes(sp.id)
                      ? SupplierPreference.Approved
                      : sourcingScenario.solution_preference.excluded_suppliers_and_stock_locations?.includes(sp.id)
                        ? SupplierPreference.Excluded
                        : SupplierPreference.NotApproved,
            }))
            .sort((a, b) => {
                const PREFERENCE_SORT_ORDER: Record<SupplierPreference, number> = {
                    [SupplierPreference.Preferred]: 0,
                    [SupplierPreference.Approved]: 1,
                    [SupplierPreference.NotApproved]: 2,
                    [SupplierPreference.Excluded]: 3,
                };
                return PREFERENCE_SORT_ORDER[a.preference] - PREFERENCE_SORT_ORDER[b.preference];
            });
    };

    const getSourcingTemplate = (): SourcingTemplateDTO | null => {
        return (
            sourcingTemplates.find((template) => template.id === sourcingScenario.origin_sourcing_template_id) ?? null
        );
    };

    const getInventoryPreferences = (): SourcingScenarioFormValues['inventoryPreferences'] => {
        return inventorySites
            .map((ip) => ({
                ...ip,
                preference: sourcingScenario.solution_preference.preferred_inventory_sites?.includes(ip.id)
                    ? SupplierPreference.Preferred
                    : sourcingScenario.solution_preference.approved_inventory_sites?.includes(ip.id)
                      ? SupplierPreference.Approved
                      : sourcingScenario.solution_preference.excluded_inventory_sites?.includes(ip.id)
                        ? SupplierPreference.Excluded
                        : SupplierPreference.NotApproved,
            }))
            .sort((a, b) => {
                const PREFERENCE_SORT_ORDER: Record<SupplierPreference, number> = {
                    [SupplierPreference.Preferred]: 0,
                    [SupplierPreference.Approved]: 1,
                    [SupplierPreference.NotApproved]: 2,
                    [SupplierPreference.Excluded]: 3,
                };
                return PREFERENCE_SORT_ORDER[a.preference] - PREFERENCE_SORT_ORDER[b.preference];
            });
    };

    return {
        general: {
            rfq: sourcingScenario.rfq,
            name: sourcingScenario.name,
            sourcingTemplate: getSourcingTemplate(),
        },
        assemblyDemands: sourcingScenario.assembly_demands.map((ad) => ({
            kind: 'existing',
            data: { id: ad.id },
        })),
        solutionPreferences: {
            leadTimePreference: {
                type: sourcingScenario.solution_preference.lead_time_preference.type,
                target: sourcingScenario.solution_preference.lead_time_preference.target,
            },
            solutionComparisonCriteria: sourcingScenario.solution_preference.use_unit_price
                ? SolutionComparisonCriteria.UnitPrice
                : SolutionComparisonCriteria.TotalPrice,
            excludedPriceTypes: {
                listPrice: sourcingScenario.solution_preference.excluded_price_types.includes(PriceType.ListPrice),
                contractPrice: sourcingScenario.solution_preference.excluded_price_types.includes(
                    PriceType.ContractPrice,
                ),
                quotePrice: sourcingScenario.solution_preference.excluded_price_types.includes(PriceType.QuotePrice),
                customerNegotiatedPrice: sourcingScenario.solution_preference.excluded_price_types.includes(
                    PriceType.CustomerNegotiatedPrice,
                ),
                purchasePrice: sourcingScenario.solution_preference.excluded_price_types.includes(
                    PriceType.PurchasePrice,
                ),
                standardPrice: sourcingScenario.solution_preference.excluded_price_types.includes(
                    PriceType.StandardPrice,
                ),
            },
            inventoryPreference: sourcingScenario.solution_preference.inventory_preference,
            packagingPreference: sourcingScenario.solution_preference.packaging_preference,
            preferDirectApiIntegrations: sourcingScenario.solution_preference.prefer_direct_api_integrations,
            excludeMismatchedPricePoints: sourcingScenario.solution_preference.exclude_mismatched_price_points,
            prioritizePreferredManufacturers: sourcingScenario.solution_preference.prioritize_preferred_manufacturers,
            preferCustomerNegotiatedPrices: sourcingScenario.solution_preference.prefer_customer_negotiated_prices,
            isTcoEnabled: sourcingScenario.solution_preference.is_tco_enabled,
        },
        solutionTagsPreferences: {
            offerAgeLimit: getOfferAgeLimit(),
        },
        supplierPreferences: getSupplierPreferences(),
        inventoryPreferences: getInventoryPreferences(),
        totalCostOfOwnershipRules: sourcingScenario.solution_preference.total_cost_of_ownership_rules,
    };
}

export function useDefaultValuesSourcingScenario({
    rfqId,
    sourcingTemplate,
    override,
}: {
    rfqId: string;
    sourcingTemplate?: SourcingTemplateDTO;
    override?: Partial<SourcingScenarioFormValues>;
}) {
    const { data: defaultSourcingTemplate } = useDefaultSourcingTemplate(SourcingTemplateTags.Sourcing);
    const { data: assemblies } = useTopLevelAssemblies(rfqId);
    const { data: supplierAndStockLocations } = useSupplierAndStockLocations();
    const { data: inventorySites } = useSites();

    const defaultValues = createDefaultSourcingScenarioFormValues({
        rfqId,
        sourcingTemplate: sourcingTemplate ?? defaultSourcingTemplate,
        assemblies,
        supplierAndStockLocations,
        inventorySites,
    });

    return { ...defaultValues, ...override };
}
